import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import * as stylex from '@stylexjs/stylex';
import { Badge, ButtonPrimitive, IconButton, MenuV2 } from '@figma/fpl-components';
import { ColorTheme } from '@figma/fpl-themes';
import { Icon24Copy, Icon24Export, Icon24Expand, Icon24More, Icon24Trash } from '@figma/fpl-icons';
import { I18N_KEYS } from '@/language/keys';
import { useToolOutputAssetActions } from './useToolOutputAssetActions';
import { isPreviewableOutput } from './ToolOutputDownload';
import { getToolOutputName, outputTypeLabels } from './ToolOutputNames';
import { ToolOutputCard } from './ToolOutputCard';
import { outputCardMarker, styles } from './ToolOutputActions.stylex';
import badgeStyles from './ToolOutputActions.module.css';
import type { DesignAppResult } from '@/types/design-app.types';

const labels = I18N_KEYS.SHARED_DESIGN_APP.OUTPUT_HISTORY;

export type ToolOutputRemovalTarget = Pick<DesignAppResult, 'id' | 'runId'>;

export interface ToolOutputActionsProps {
  result: DesignAppResult;
  recipeName: string;
  /** Without it, the menu has no Remove item. */
  onRemove?: (target: ToolOutputRemovalTarget) => void;
  disableRemove: boolean;
  onExpand?: () => void;
  suspendMediaPlayback?: boolean;
  /** CSS length, e.g. `'50vh'` or `'200px'`. Applies to images with known dimensions. */
  imageMaxHeight?: string;
}

export function ToolOutputActions({
  result,
  recipeName,
  onRemove,
  disableRemove,
  onExpand,
  suspendMediaPlayback,
  imageMaxHeight,
}: ToolOutputActionsProps) {
  const { t } = useTranslation();
  const name = getToolOutputName(result, t);
  const { link, canDownload, download, copyLink } = useToolOutputAssetActions({
    result,
    workflowName: recipeName,
    name,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const { manager, getTriggerProps } = MenuV2.useMenu({ open: menuOpen, onOpenChange: setMenuOpen });

  if (!Object.hasOwn(outputTypeLabels, result.type)) {
    return (
      <div role="group" aria-label={name} {...stylex.props(styles.card)}>
        <ToolOutputCard result={result} suspendMediaPlayback={suspendMediaPlayback} />
      </div>
    );
  }

  const { Icon } = outputTypeLabels[result.type];
  const imageMaxHeightStyle =
    imageMaxHeight && result.type === 'image' && result.width && result.height
      ? styles.maxMediaHeight(imageMaxHeight, result.width / result.height)
      : undefined;

  return (
    <div role="group" aria-label={name} {...stylex.props(styles.card, imageMaxHeightStyle, outputCardMarker)}>
      {result.type === 'image' && onExpand ? (
        <ButtonPrimitive
          type="button"
          aria-label={name}
          aria-haspopup="dialog"
          onClick={onExpand}
          {...stylex.props(styles.imageTrigger)}
        >
          <ToolOutputCard result={result} />
        </ButtonPrimitive>
      ) : (
        <ToolOutputCard result={result} suspendMediaPlayback={suspendMediaPlayback} />
      )}
      <div aria-hidden="true" {...stylex.props(styles.gradient, styles.overlay, menuOpen && styles.visible)} />
      <ColorTheme brand="weave" mode="dark">
        <div {...stylex.props(styles.toolbar, styles.overlay, menuOpen && styles.visible)}>
          <span
            className={clsx(
              badgeStyles.title,
              stylex.props(styles.title, styles.interactive, menuOpen && styles.interactiveVisible).className,
            )}
            title={name}
          >
            <Badge variant="defaultFilled" size="lg" iconPrefix={<Icon />}>
              {name}
            </Badge>
          </span>
          <div data-output-actions {...stylex.props(styles.actions)}>
            <span {...stylex.props(styles.action, styles.interactive, menuOpen && styles.interactiveVisible)}>
              <IconButton aria-label={t(labels.DOWNLOAD, { name })} disabled={!canDownload} onClick={download}>
                <Icon24Export />
              </IconButton>
            </span>
            <span {...stylex.props(styles.action, styles.interactive, menuOpen && styles.interactiveVisible)}>
              <IconButton
                aria-label={t(labels.EXPAND, { name })}
                disabled={!onExpand || !isPreviewableOutput(result)}
                onClick={onExpand}
              >
                <Icon24Expand />
              </IconButton>
            </span>
            <span {...stylex.props(styles.action, styles.interactive, menuOpen && styles.interactiveVisible)}>
              <IconButton aria-label={t(labels.MORE, { name })} {...getTriggerProps()}>
                <Icon24More />
              </IconButton>
            </span>
          </div>
        </div>
      </ColorTheme>
      <MenuV2.Root
        manager={manager}
        position={{ area: 'bottom span-left' }}
        colorTheme={{ brand: 'weave', mode: 'dark' }}
      >
        {result.type !== 'text' && (
          <MenuV2.Item disabled={!link} lead={<Icon24Copy />} onClick={copyLink}>
            {t(I18N_KEYS.GENERAL.COPY_LINK)}
          </MenuV2.Item>
        )}
        {onRemove && (
          <MenuV2.Item
            variant="destructive"
            disabled={disableRemove}
            lead={<Icon24Trash />}
            onClick={() => onRemove({ id: result.id, runId: result.runId })}
          >
            {t(labels.REMOVE)}
          </MenuV2.Item>
        )}
      </MenuV2.Root>
    </div>
  );
}
